/**
 * Avisos suaves (no bloqueantes) para contactos: apellidos ES, documento, teléfono.
 * Espejo en backend/movil/lib/contactoSoftValidation.js — mantener alineados.
 */

const LETRAS_DNI = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_LETRAS_CONTROL = 'JABCDEFGHI';
const CIF_SOLO_LETRA = 'KPQS';
const CIF_SOLO_DIGITO = 'ABEH';

function limpiarDoc(raw) {
  return String(raw || '').trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
}

function normalizarTexto(s) {
  try {
    return String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  } catch {
    return String(s || '').toLowerCase().trim();
  }
}

/** ¿Parece CIF/NIF empresa? (misma idea que propietarioCamposConfig / móvil). */
export function docPareceCif(doc) {
  const s = limpiarDoc(doc);
  if (!s) return false;
  if (/^\d{8}[A-Z]$/.test(s)) return false;
  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) return false;
  return /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[\dA-J]$/.test(s)
    || (/^[ABCDEFGHJKLMNPQRSUVW]/.test(s) && s.length >= 8 && s.length <= 10);
}

function letraControlDni(numStr, prefijoNie) {
  let n = String(numStr || '').replace(/\D/g, '');
  if (prefijoNie) {
    const map = { X: '0', Y: '1', Z: '2' };
    const p = String(prefijoNie).toUpperCase();
    if (!map[p] || n.length !== 7) return null;
    n = map[p] + n;
  }
  if (n.length !== 8) return null;
  const idx = parseInt(n, 10) % 23;
  if (Number.isNaN(idx)) return null;
  return LETRAS_DNI[idx];
}

function dniNieLetraCoherente(doc) {
  const s = limpiarDoc(doc);
  if (/^\d{8}[A-Z]$/.test(s)) {
    return letraControlDni(s.slice(0, 8)) === s.slice(8);
  }
  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) {
    return letraControlDni(s.slice(1, 8), s[0]) === s.slice(8);
  }
  return false;
}

function cifControlCoherente(doc) {
  const s = limpiarDoc(doc);
  if (!/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[\dA-J]$/.test(s)) return false;
  const org = s[0];
  const digits = s.slice(1, 8);
  const control = s[8];
  let sum = 0;
  // Posiciones 1,3,5,7 (1-index / i par en 0-index): ×2 y suma de dígitos.
  // Posiciones 2,4,6: se suman tal cual. (Bug previo: pares/impares invertidos → CIF válidos fallaban.)
  for (let i = 0; i < 7; i += 1) {
    const n = parseInt(digits[i], 10);
    if (i % 2 === 0) {
      const d = n * 2;
      sum += Math.floor(d / 10) + (d % 10);
    } else {
      sum += n;
    }
  }
  const cd = (10 - (sum % 10)) % 10;
  const expectedLetter = CIF_LETRAS_CONTROL[cd];
  const expectedDigit = String(cd);
  if (CIF_SOLO_LETRA.includes(org)) return control === expectedLetter;
  if (CIF_SOLO_DIGITO.includes(org)) return control === expectedDigit;
  return control === expectedDigit || control === expectedLetter;
}

export function etiquetaDocumento(doc) {
  const s = limpiarDoc(doc);
  if (/^[XYZ]\d{7}[A-Z]?$/.test(s)) return 'NIE';
  if (docPareceCif(s)) return 'CIF';
  return 'DNI';
}

export function documentoPareceCompleto(doc) {
  const s = limpiarDoc(doc);
  if (!s) return false;
  if (/^\d{8}[A-Z]$/.test(s)) return true;
  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) return true;
  if (/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[\dA-J]$/.test(s)) return true;
  return false;
}

/** Valor no vacío que parece DNI/NIE/CIF español pero aún no tiene forma final (9 caracteres). */
export function documentoPareceIncompleto(doc) {
  const s = limpiarDoc(doc);
  if (!s) return false;
  if (documentoPareceCompleto(s)) return false;
  // DNI en curso (solo números o falta letra final)
  if (/^\d{1,8}[A-Z]?$/.test(s) && s.length < 9) return true;
  // NIE en curso
  if (/^[XYZ][0-9]{0,7}[A-Z]?$/i.test(s) && s.length < 9) return true;
  // CIF en curso
  if (/^[ABCDEFGHJKLMNPQRSUVW][0-9]{0,7}[\dA-J]?$/i.test(s) && s.length < 9) return true;
  // Longitud ≥ 9 pero no encaja en formato completo
  if (s.length >= 9) {
    if (/^\d/.test(s) && !/^\d{8}[A-Z]$/.test(s)) return true;
    if (/^[XYZ]/i.test(s) && !/^[XYZ]\d{7}[A-Z]$/i.test(s)) return true;
    if (/^[ABCDEFGHJKLMNPQRSUVW]/i.test(s) && !/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[\dA-J]$/i.test(s)) return true;
  }
  return false;
}

export function documentoPareceInvalido(doc) {
  const s = limpiarDoc(doc);
  if (!s) return false;
  if (documentoPareceCompleto(s)) {
    if (/^\d{8}[A-Z]$/.test(s) || /^[XYZ]\d{7}[A-Z]$/.test(s)) {
      return !dniNieLetraCoherente(s);
    }
    if (docPareceCif(s)) return !cifControlCoherente(s);
    return true;
  }
  // En curso (falta letra/control): no avisar hasta que tenga longitud completa
  if (s.length < 9) return false;
  return documentoPareceIncompleto(s);
}

export function pareceContactoEspanol(ctx = {}) {
  const doc = limpiarDoc(ctx.dni || ctx.dni_nie || '');
  if (doc && /^\d{8}[A-Z]$/.test(doc)) return true;
  const idi = String(ctx.idioma || '').toLowerCase();
  if (idi === 'es' || idi.startsWith('es-')) return true;
  const nat = normalizarTexto(ctx.nacionalidad || '');
  if (nat && (/espana|spain/.test(nat) || nat === 'es')) return true;
  const pais = String(ctx.pais || ctx.domicilio_pais || '').toUpperCase();
  if (pais === 'ES' || pais === 'ESP' || pais === 'ESPAÑA' || pais === 'SPAIN') return true;
  return false;
}

export function apellidosParecenIncompletos(apellidos, ctx = {}) {
  const ape = String(apellidos || '').trim();
  if (!ape) return false;
  if (docPareceCif(ctx.dni || ctx.dni_nie || '')) return false;
  if (!pareceContactoEspanol(ctx)) return false;
  const partes = ape.split(/\s+/).filter(Boolean);
  return partes.length < 2;
}

function normalizarTelefono(raw) {
  if (raw == null || raw === '') return null;
  let s = String(raw).trim().replace(/[^\d+]/g, '');
  if (s.startsWith('00')) s = `+${s.slice(2)}`;
  if (s.startsWith('+')) {
    const digits = s.slice(1).replace(/\D/g, '');
    if (digits.startsWith('34') && digits.length === 11 && /^34[6789]\d{8}$/.test(digits)) {
      return `+${digits}`;
    }
    if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;
    return null;
  }
  const digits = s.replace(/\D/g, '');
  if (/^[6789]\d{8}$/.test(digits)) return digits;
  if (digits.length >= 10 && digits.length <= 15) return digits;
  return null;
}

export function telefonoPareceInvalido(telefono) {
  const raw = String(telefono || '').trim();
  if (!raw) return false;
  if (/^(\d)\1{8,}$/.test(raw.replace(/\D/g, ''))) return true;
  return !normalizarTelefono(raw);
}

/** Formato de email incompleto o incorrecto (aviso suave, no bloqueante). */
export function emailPareceInvalido(email) {
  const raw = String(email || '').trim();
  if (!raw) return false;
  if (/\s/.test(raw) || raw.length > 254) return true;
  // local@dominio.tld — exige al menos un punto en el dominio
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return true;
  if (/@\.|\.@|@@|\.$|^\.|\.\./.test(raw)) return true;
  return false;
}

export function mensajeAvisoApellidos() {
  return 'Atención, el contacto para español, se esperan 2 apellidos. Revísalo';
}

export function mensajeAvisoDocumento(doc) {
  const et = etiquetaDocumento(doc);
  return `Atención, el ${et} parece incorrecto. Revísalo`;
}

export function mensajeAvisoTelefono() {
  return 'Atención, el teléfono parece incorrecto. Revísalo';
}

export function mensajeAvisoEmail() {
  return 'Atención, el email parece incorrecto. Revísalo';
}

export function buildAvisoContactoSoft(tipo, value, ctx = {}) {
  if (tipo === 'apellidos' || tipo === 'apellido') {
    if (!apellidosParecenIncompletos(value, ctx)) return null;
    return {
      tipo: 'apellidos',
      mensaje: mensajeAvisoApellidos(),
      fieldLabel: 'Apellidos',
      dismissKey: 'apellidos',
      inputType: 'text',
    };
  }
  if (tipo === 'dni' || tipo === 'dni_nie') {
    if (!documentoPareceInvalido(value)) return null;
    return {
      tipo: 'documento',
      mensaje: mensajeAvisoDocumento(value),
      fieldLabel: etiquetaDocumento(value),
      dismissKey: 'documento',
      inputType: 'text',
    };
  }
  if (tipo === 'telefono' || tipo === 'telefono_secundario') {
    if (!telefonoPareceInvalido(value)) return null;
    return {
      tipo: 'telefono',
      mensaje: mensajeAvisoTelefono(),
      fieldLabel: 'Teléfono',
      dismissKey: 'telefono',
      inputType: 'tel',
    };
  }
  if (tipo === 'email' || tipo === 'correo' || tipo === 'mail') {
    if (!emailPareceInvalido(value)) return null;
    return {
      tipo: 'email',
      mensaje: mensajeAvisoEmail(),
      fieldLabel: 'Email',
      dismissKey: 'email',
      inputType: 'email',
    };
  }
  return null;
}

/**
 * ¿Avisar ya en el tecleo?
 * Solo con documento completo (letra/control) o basura ≥9 chars.
 * No avisar con 8 dígitos sin letra: el agente aún la está poniendo.
 */
export function debeRevisarDocumentoEnInput(value) {
  const s = limpiarDoc(value);
  if (!s) return false;
  if (documentoPareceCompleto(value)) return documentoPareceInvalido(value);
  if (s.length >= 9) return documentoPareceIncompleto(value);
  return false;
}
